import { NodeAxios } from '@/services/api'

export type HeftSubjectType = 'person' | 'company' | 'product' | 'group'

export type CuratedClaimStatus = 'supported' | 'hypothesis' | 'missing' | 'conflicted'

export type HeftProfileAssetType = 'story' | 'star_point' | 'beacon' | 'ethos' | 'identity_dimension' | 'fact' | 'quote'

export interface HeftProfileAssetRef {
    asset_type: HeftProfileAssetType
    asset_id: string
    label?: string
}

export type HeftProfileSelectionMode = 'all' | 'curated'

export interface HeftIdentityVisual {
    url: string
    thumbnail_url?: string
    media_document_id?: string
    alt: string
    prompt: string
    source: 'generated'
    generated_at: string
}

export interface SourceMoment {
    text: string
    story_variant_id?: string
    fact_id?: string
}

export interface CuratedStarPoint {
    _id: string
    lens: string
    bucket?: string
    label: string
    short_label?: string
    definition: string
    status: CuratedClaimStatus
    confidence?: number
    supporting_story_variant_ids: string[]
    supporting_fact_ids: string[]
    source_moments: SourceMoment[]
    missing_evidence_prompt?: string
    recommended_interview_pack_ids?: string[]
    user_confirmed?: boolean
    user_hidden?: boolean
    sort_order: number
    created_by?: string
    updated_by?: string
}

export interface IdentityFacet {
    _id: string
    bucket: string
    lens: string
    label: string
    short_label?: string
    definition: string
    status: CuratedClaimStatus
    confidence?: number
    supporting_star_point_ids: string[]
    supporting_story_variant_ids: string[]
    supporting_fact_ids: string[]
    source_moments: SourceMoment[]
    missing_evidence_prompt?: string
    recommended_interview_pack_ids?: string[]
    user_confirmed?: boolean
    user_hidden?: boolean
    sort_order: number
    created_by?: string
    updated_by?: string
}

export interface HeftProfileView {
    _id: string
    name: string
    description?: string
    lens: string
    included_story_variant_ids: string[]
    included_fact_ids: string[]
    selected_asset_refs?: HeftProfileAssetRef[]
    asset_selection_mode?: HeftProfileSelectionMode
    identity_visual?: HeftIdentityVisual
    positioning_statement?: string
    credibility_markers: string[]
    relatability_markers: string[]
    intrigue_markers: string[]
    star_points: CuratedStarPoint[]
    identity_facets: IdentityFacet[]
}

export interface HeftProfileCuration {
    _id: string
    subject_id: string
    subject_type: HeftSubjectType
    primary_view_id?: string
    views: HeftProfileView[]
}

export const heftProfileCurationService = {
    getOrCreateCuration(params: { subjectType: HeftSubjectType; subjectId: string; ensure?: boolean }) {
        const query = params.ensure ? '?ensure=1' : ''
        return NodeAxios.get(`/heft-profile/${params.subjectType}/${params.subjectId}/curation${query}`) as Promise<{
            success: boolean
            data: HeftProfileCuration
        }>
    },

    createView(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        name: string
        lens: string
        description?: string
        selected_asset_refs?: HeftProfileAssetRef[]
        asset_selection_mode?: HeftProfileSelectionMode
    }) {
        return NodeAxios.post(`/heft-profile/${params.subjectType}/${params.subjectId}/views`, {
            curationId: params.curationId,
            name: params.name,
            lens: params.lens,
            description: params.description,
            selected_asset_refs: params.selected_asset_refs ?? [],
            asset_selection_mode: params.asset_selection_mode ?? 'all',
            editIntent: 'presentation',
            compatibilityWrite: true,
        }) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    setPrimaryView(params: { subjectType: HeftSubjectType; subjectId: string; curationId: string; viewId: string }) {
        return NodeAxios.post(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/primary`,
            {
                curationId: params.curationId,
                editIntent: 'usage',
                compatibilityWrite: true,
            }
        ) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    generateIdentityVisual(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        prompt: string
    }) {
        return NodeAxios.post(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/identity-visual`,
            {
                curationId: params.curationId,
                prompt: params.prompt,
                editIntent: 'presentation',
                compatibilityWrite: true,
            }
        ) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    patchView(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        patch: Partial<Pick<HeftProfileView, 'name' | 'description' | 'lens' | 'positioning_statement'>>
    }) {
        return NodeAxios.patch(`/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}`, {
            curationId: params.curationId,
            patch: params.patch,
            editIntent: 'presentation',
            compatibilityWrite: true,
        }) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    deleteView(params: { subjectType: HeftSubjectType; subjectId: string; curationId: string; viewId: string }) {
        return NodeAxios.delete(`/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}`, {
            data: {
                curationId: params.curationId,
                editIntent: 'presentation',
                compatibilityWrite: true,
            },
        }) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    addFacet(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        facet: Omit<IdentityFacet, '_id'>
    }) {
        return NodeAxios.post(`/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/facets`, {
            curationId: params.curationId,
            facet: params.facet,
            editIntent: 'presentation',
            compatibilityWrite: true,
        }) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    patchFacet(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        facetId: string
        patch: Partial<IdentityFacet>
        editIntent: 'presentation' | 'usage'
    }) {
        return NodeAxios.patch(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/facets/${params.facetId}`,
            {
                curationId: params.curationId,
                patch: params.patch,
                editIntent: params.editIntent,
                compatibilityWrite: true,
            }
        ) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    deleteFacet(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        facetId: string
    }) {
        return NodeAxios.delete(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/facets/${params.facetId}`,
            {
                data: {
                    curationId: params.curationId,
                    editIntent: 'presentation',
                    compatibilityWrite: true,
                },
            }
        ) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    addStarPoint(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        starPoint: Omit<CuratedStarPoint, '_id'>
    }) {
        return NodeAxios.post(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/star-points`,
            {
                curationId: params.curationId,
                starPoint: params.starPoint,
                editIntent: 'presentation',
                compatibilityWrite: true,
            }
        ) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    patchStarPoint(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        starPointId: string
        patch: Partial<CuratedStarPoint>
        editIntent: 'presentation' | 'usage'
    }) {
        return NodeAxios.patch(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/star-points/${params.starPointId}`,
            {
                curationId: params.curationId,
                patch: params.patch,
                editIntent: params.editIntent,
                compatibilityWrite: true,
            }
        ) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },

    deleteStarPoint(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        starPointId: string
    }) {
        return NodeAxios.delete(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/star-points/${params.starPointId}`,
            {
                data: {
                    curationId: params.curationId,
                    editIntent: 'presentation',
                    compatibilityWrite: true,
                },
            }
        ) as Promise<{ success: boolean; data: HeftProfileCuration }>
    },
}

export const heftProfileContextMembershipService = {
    list(params: { subjectType: HeftSubjectType; subjectId: string; curationId: string; viewId: string }) {
        return NodeAxios.get(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/memberships?curationId=${encodeURIComponent(params.curationId)}`
        ) as Promise<{ success: boolean; data: HeftProfileAssetRef[] }>
    },
    add(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        ref: HeftProfileAssetRef
    }) {
        return NodeAxios.post(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/memberships`,
            {
                curationId: params.curationId,
                ref: params.ref,
                editIntent: 'presentation',
                compatibilityWrite: true,
            }
        ) as Promise<{ success: boolean; data: HeftProfileAssetRef[] }>
    },
    remove(params: {
        subjectType: HeftSubjectType
        subjectId: string
        curationId: string
        viewId: string
        asset_type: string
        asset_id: string
    }) {
        return NodeAxios.delete(
            `/heft-profile/${params.subjectType}/${params.subjectId}/views/${params.viewId}/memberships`,
            {
                data: {
                    curationId: params.curationId,
                    asset_type: params.asset_type,
                    asset_id: params.asset_id,
                    editIntent: 'presentation',
                    compatibilityWrite: true,
                },
            }
        ) as Promise<{ success: boolean; data: HeftProfileAssetRef[] }>
    },
    copy(params: { sourceCurationId: string; sourceViewId: string; targetCurationId: string; targetViewId: string }) {
        return NodeAxios.post('/heft-profile/context-memberships/copy', {
            ...params,
            editIntent: 'presentation',
            compatibilityWrite: true,
        }) as Promise<{
            success: boolean
            data: HeftProfileAssetRef[]
        }>
    },
    move(params: { sourceCurationId: string; sourceViewId: string; targetCurationId: string; targetViewId: string }) {
        return NodeAxios.post('/heft-profile/context-memberships/move', {
            ...params,
            editIntent: 'presentation',
            compatibilityWrite: true,
        }) as Promise<{
            success: boolean
            data: HeftProfileAssetRef[]
        }>
    },
}
